import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import { useEffect, useRef } from "react";

import { useAppState } from "../../hooks/useAppState";
import { createTelemetryEvent } from "../../telemetry/Telemetry";
import { telemetryChannelName } from "../../util/constants";
import { useViewport, View } from "../Viewport/useViewport";
import ButtonExitSlides from "./ButtonExitSlides/ButtonExitSlides";
import { Container, ImageWrapper, InnerContainer } from "./SlideThumbnails.elements";

export const SlideThumbnails = (): React.ReactElement => {
  const { module, setSlide, slide: currentSlide, course } = useAppState();
  const firedModuleIds = useRef(new Set<string>());

  const { changeView } = useViewport();

  // Get section number from course sections (index + 1)
  const sectionNumber = course?.sections
    ? course.sections.findIndex(section => section.id === module?.sectionId) + 1
    : 1;

  // Fire telemetry event when slide thumbnails view is rendered
  useEffect(() => {
    if (module && module.type === "CONTENT" && module.id && !firedModuleIds.current.has(module.id)) {
      firedModuleIds.current.add(module.id);

      // Use requestAnimationFrame to ensure telemetry listeners are set up
      // This is more reliable than setTimeout and ensures DOM is ready
      requestAnimationFrame(() => {
        createTelemetryEvent({
          feature: "playback",
          action: "view slide thumbnails",
          action_meta: `section: ${sectionNumber}`,
        }).fire(telemetryChannelName);
      });
    }
  }, [module, sectionNumber]);

  if (!module) return <div>No module selected.</div>;
  if (module.type !== "CONTENT") return <div>{module.type}</div>;

  const gotoSlide = (slideId: string) => {
    setSlide(slideId);
    changeView(View.Video);
  };

  const handleSlideClick = (slideId: string, slideNumber: number) => () => {
    // Fire telemetry event for clicking individual thumbnail
    createTelemetryEvent({
      feature: "playback",
      action: "click slide thumbnail",
      action_meta: `section: ${sectionNumber}, clicked slide: ${slideNumber}`,
    }).fire(telemetryChannelName);

    gotoSlide(slideId);
  };

  return (
    <Container data-testid="slide-thumbnails">
      <InnerContainer>
        <ImageList style={{ gridTemplateColumns: "repeat(auto-fit, minmax(165px,1fr))", width: "100%" }}>
          {module.slides.map((slide, index) => (
            <ImageListItem key={slide.id}>
              <ImageWrapper
                className={currentSlide?.id === slide.id ? "selected" : ""}
                tabIndex={1}
                onClick={handleSlideClick(slide.id, slide.number)}
                autoFocus={index === 0}
                aria-label={`Video ${index + 1} of ${module.slides.length}: ${slide.name}`}
              >
                <img src={`${slide.thumbnailPath}`} alt={slide.name} loading="lazy" style={{ cursor: "pointer" }} />
              </ImageWrapper>
            </ImageListItem>
          ))}
        </ImageList>
      </InnerContainer>
      <ButtonExitSlides></ButtonExitSlides>
    </Container>
  );
};
